import { MYKYC_API_BASE } from '../config/api';
import { intakePlan, intakeReviewValueError, sameIntakeValue, sameIntakePersonName, confirmedCvFields } from './clientDocumentIntake';

export const FIRST_INTAKE_MODE = 'with_kyc_individuals_first_prompt';
export const FIRST_INTAKE_CV_MODE = 'with_kyc_individuals_first_prompt_cv';
export const isFirstIntakeMode = mode => [FIRST_INTAKE_MODE, FIRST_INTAKE_CV_MODE].includes(mode);
const fields = new Set(['givenNames', 'familyName', 'clientName', 'dateOfBirth', 'nationality', 'address', 'city', 'country']);

// extra: company upload-first only, { country, companyType } select the corporate document list.
export async function publicIntakeRequest(action, { clientId, token, files, ...extra }, signal) {
  const response = await fetch(`${MYKYC_API_BASE}/myKyc/clients/document-intake/public-${action}?clientId=${encodeURIComponent(clientId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ files, ...extra }), signal,
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.ok) throw new Error(result?.error?.message || 'Document processing is unavailable. Retry or continue manually.');
  return result;
}

// Individuals (companies use the company document reading and its confirmation window).
export function publicIntakePlan(documents, form, separateNames, { includeCv = false } = {}) {
  // Magic-link intake replaces existing details. Only disagreements between
  // the uploaded ID and proof of address require a choice.
  const allowed = fields;
  const identityDocuments = documents.filter(doc => ['id_document', 'proof_of_address'].includes(doc.category));
  const plan = intakePlan(identityDocuments, { clientType: 'individual' }, [], { separateNames });
  const values = Object.fromEntries([
    ...Object.entries(plan.autoFill),
    ...Object.entries(plan.autoKyc).map(([key, item]) => [key, item.value]),
  ].filter(([key, value]) => allowed.has(key) && !intakeReviewValueError(key, value)));
  const review = [];
  for (const row of plan.review.filter(row => allowed.has(row.field))) {
    const options = row.options.filter(option => !intakeReviewValueError(row.field, option.value));
    const same = row.field === 'clientName' ? sameIntakePersonName : sameIntakeValue;
    if (options.some(option => !same(option.value, options[0]?.value))) {
      review.push({ ...row, options, issues: [], uncertain: false, currentValue: '', existingValueDiffers: false });
    } else if (options.length) {
      values[row.field] = options[0].value;
    }
  }
  if (includeCv) {
    const cv = [...documents].reverse().find(doc => doc.category === 'cv' && doc.status === 'extracted');
    const profile = !cv?.extraction?.multipleSubjects && cv?.extraction?.cvProfile;
    if (profile) {
      // Reuse the CV field mapping. Only evidence-backed source categories are
      // preselected; amounts and other source-of-wealth details stay unchanged.
      const selectedSources = (profile.suggestions || []).filter(item =>
        item.uncertain === false && item.confidence >= .85 && item.evidence?.trim() && item.explanation?.trim()
      ).map(item => item.category);
      const cvValues = confirmedCvFields({ ...profile, selectedSources }, 'individual');
      if (cvValues.businessActivity) values.businessActivity = cvValues.businessActivity;
      for (const category of cvValues.sourceOfWealth) values[`wealth:${category}`] = category;
    }
  }
  return { documents, review, values };
}

export function resolvePublicIntake(plan, choices) {
  const values = { ...plan.values };
  for (const row of plan.review) {
    const choice = choices[row.field];
    const option = typeof choice === 'object' && choice &&
      [...row.options, ...row.issues].some(item => item.documentId === choice.documentId) &&
      !intakeReviewValueError(row.field, choice.value)
      ? choice : row.options.find(item => `${item.documentId}:${item.value}` === choice);
    if (option && fields.has(row.field)) values[row.field] = option.value;
  }
  return values;
}
